import React, { useMemo } from "react";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { buildAddress, buildUser, buildWorkerProfile } from "../../factories";
import { ProfileType, WorkerProfileInput, WorkingPermitType } from "../../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedProvider } from "@apollo/client/testing/react";
import CivilStateEditorScreen from "../../../screens/profile-editor/CivilStateEditor";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { UPDATE_WORKER_PROFILE } from "../../../graphql/core";
import { StoryStackNavigatorContainer } from "../../display-helpers";

const Stack = createNativeStackNavigator<{
    CivilStateEdit: undefined;
}>();

// A cross-border worker's home, outside Switzerland.
const frenchAddress = buildAddress({
    formatted: "Rue de la République 1, 74100 Annemasse",
    locality: {
        name: "Annemasse",
        postalCode: "74100",
        state: {
            code: "74",
            name: "Haute-Savoie",
            country: { code: "FR", name: "France" },
        },
    },
});

const StoryComponent = ({
    workPermit,
    nationality,
    livesAbroad,
}: {
    workPermit?: WorkingPermitType;
    nationality: string;
    livesAbroad?: boolean;
}) => {
    const user = useMemo(() => {
        const user = buildUser({
            workerProfile: buildWorkerProfile({
                swissOrHasPermitAnswer: !!workPermit,
                lastWorkPermit: { type: workPermit ?? null, validFrom: null, validTo: null },
                avsNumber: null,
            }),
            nationality,
            ...(livesAbroad ? { address: frenchAddress } : {}),
        });
        return user;
    }, [livesAbroad, nationality, workPermit]);

    const mocks = useMemo(
        () => [
            {
                request: {
                    query: UPDATE_WORKER_PROFILE,
                    variables: () => true,
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: ({ changes }: { changes: WorkerProfileInput }) => {
                    // eslint-disable-next-line no-console
                    console.log("UPDATE_WORKER_PROFILE.changes", changes);
                    return {
                        data: {
                            updateWorkerProfile: buildWorkerProfile({
                                children: [],
                                hasFiscalAttestation: false,
                            }),
                        },
                    };
                },
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Worker}>
                <MockedProvider mocks={mocks}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="CivilStateEdit">
                            <Stack.Screen
                                name="CivilStateEdit"
                                component={CivilStateEditorScreen}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/ProfileEditor/CivilStateEditor",
    component: StoryComponent,
    argTypes: {
        workPermit: {
            options: Object.values(WorkingPermitType),
            control: { type: "radio" },
            defaultValue: WorkingPermitType.PermitB,
        },
        nationality: {
            options: ["CH", "FR", "US"],
            control: { type: "radio" },
        },
        livesAbroad: {
            control: { type: "boolean" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const FR_B: Story = { args: { workPermit: WorkingPermitType.PermitB, nationality: "FR" } };
export const CH: Story = { args: { workPermit: undefined, nationality: "CH" } };
export const FR_no_permit: Story = { args: { workPermit: undefined, nationality: "FR" } };
export const FR_G_cross_border: Story = {
    args: { workPermit: WorkingPermitType.PermitG, nationality: "FR", livesAbroad: true },
};
